"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const saveAs = require("file-saver").saveAs;
const globalSpace = typeof window === "undefined" ? global : window;
const zip = globalSpace.zip;
const FileConstructor = require("./File");

/**
 * Zip
 */
class Zip extends EventEmitter {
    /**
     * @constructor
     * @param {String} fileName
     */
    constructor(fileName) {
        super();
        if (_.isUndefined(fileName) || !_.isString(fileName)) {
            throw new Error("ZipFile fileName required");
        }
        zip.useWebWorkers = false;
        this._zipFileName = fileName;
        this._blobDefs = [];
    }

    /***************************************************************/
    /* PRIVATE METHODS
    /***************************************************************/

    /**
     * @typedef {Object} BlobDef
     * @property {String} fileName
     * @property {Blob} blob
     */

    /**
     * Adds a BlobDef to the BlobDefs
     * @param {BlobDef} blobDef
     * @private
     */
    _addBlobDef(blobDef) {
        this._blobDefs.push(blobDef);
    }

    /**
     * Zip the Blobs
     * @private
     * @param {Array.<Blob>} blobs
     * @param {callback} resolve
     * @param {callback} reject
     */
    _zipBlobs(blobs, resolve, reject) {
        zip.createWriter(
            new zip.BlobWriter("application/zip"),
            (zipWriter) => {
                let index = 0;
                let prevProgIdx = 0;
                let next = () => {
                    let bLen = blobs.length;
                    if (index < bLen) {
                        zipWriter.add(
                            blobs[index].fileName,
                            new zip.BlobReader(blobs[index].blob),
                            () => {
                                index++;
                                next();
                            },
                            (cIdx, tIdx) => {
                                if (cIdx > prevProgIdx) {
                                    // because progress is not called in order
                                    let data = {
                                        fileIndex: index,
                                        fileTotal: bLen,
                                        filePercentage:
                                            tIdx === cIdx ? 100 : Math.ceil(100 / (bLen / index)),
                                        fileName: blobs[index].fileName,
                                        currentIndex: cIdx,
                                        totalIndex: tIdx,
                                        percentage: Math.ceil(100 / (tIdx / cIdx)),
                                    };
                                    this.emit("zip.progress", data);
                                    prevProgIdx = cIdx;
                                }
                            }
                        );
                    } else {
                        zipWriter.close(resolve);
                    }
                };
                next();
            },
            reject
        );
    }

    /***************************************************************/
    /* PUBLIC API
    /***************************************************************/

    /**
     * Add a string File
     * @param {String|Array.<String>} string - Required
     * @param {String} fileName - Required
     * @param {String} type - Optional - http://www.freeformatter.com/mime-types-list.html#mime-types-list
     * @returns {Zip}
     */
    addStringFile(string, fileName, type = "text/plain") {
        if (_.isString(string)) {
            if (_.isString(fileName)) {
                let data = _.isArray(string) ? string : [string];
                let blob = new Blob(data, {type: type});
                this._addBlobDef({blob: blob, fileName: fileName});
            } else {
                console.error("Invalid fileName parameter.");
            }
        } else {
            console.error("Invalid string parameter.");
        }
        return this;
    }

    /**
     * Add a Blob file
     * @param {Blob} blob - Required
     * @param {String} fileName - Required
     * @returns {Zip}
     */
    addBlobFile(blob, fileName) {
        if (blob instanceof Blob) {
            if (_.isString(fileName)) {
                this._addBlobDef({blob: blob, fileName: fileName});
            } else {
                console.error("Unable to add blob - fileName is required and should be a string");
            }
        } else {
            console.error("Unable to add blob - Invalid blob type");
        }
        return this;
    }

    /**
     * Add a Steve.io.File file
     * @param {FileConstructor} file
     */
    addFile(file) {
        if (file instanceof FileConstructor) {
            let bd = {blob: file.getBlob(), fileName: file.name};
            this._addBlobDef(bd);
        } else {
            console.error(
                "Unable to add file - Expected file to be an instance of Steve.io.File class."
            );
        }
    }

    /**
     * Get the Zipped file as a Blob instance
     * @returns {Promise.<Blob>} ZipBlob
     */
    getZipBlob() {
        return new Promise((resolve, reject) => {
            this._zipBlobs(
                this._blobDefs,
                (zippedBlob) => {
                    resolve(zippedBlob);
                },
                reject
            );
        });
    }

    /**
     * Download the Zip File
     * @returns {Promise}
     */
    download() {
        return new Promise((resolve, reject) => {
            this._zipBlobs(
                this._blobDefs,
                (zippedBlob) => {
                    saveAs(zippedBlob, this._zipFileName);
                    resolve(zippedBlob);
                },
                reject
            );
        });
    }
}
module.exports = Zip;
